<template>
  <AppModal 
    v-model="value.showModal" 
    :headerName="$t('New contract')"
    :footer-hide="false"
    @click:submit="submitForm"
    @click:cancel="triggerCancel"
    width="700" 
    :loading="loading" 
    class="contract-modal"
  >
    <Form ref="contractForm" :model="value.form" :rules="formRules" @submit.native.prevent="submitForm" >

      <Row class="mt-5">
        <i-col :md="12">
          <strong> {{ $t("Identification") }}</strong>
        </i-col>
        <i-col :md="12">
          <p>{{ $t("Contract number") }}</p>
          <FormItem>
            <i-input
              :placeholder="value.form.contract_identifier ? value.form.contract_identifier: $t('Auto generated')"
              :disabled="true"
            />
          </FormItem>
          <p class="mt-4">{{ $t("Contract name") }}</p>
          <FormItem prop="contract_name">
            <Input
              v-model="value.form.contract_name"
              :placeholder="$t('Contract name')"></Input>
          </FormItem>
        </i-col>
      </Row>
      <Row class="mt-5">
        <i-col :md="12">
          <strong> {{ $t("Date") }} </strong>
        </i-col>
        <i-col :md="12">
          <p>{{ $t("Departure Date") }}</p>
          <FormItem prop="contract_start_date">
            <DatePicker type="date" confirm :placeholder="$t('Specific date')" style="width: 100%"
              v-model="value.form.contract_start_date" format="yyyy/MM/dd"></DatePicker>
          </FormItem>
          <p class="mt-4">{{ $t("Return date") }}</p>
          <FormItem prop="contract_end_date">
            <DatePicker type="date" confirm :placeholder="$t('Specific date')" style="width: 100%"
              v-model="value.form.contract_end_date" format="yyyy/MM/dd"></DatePicker>
          </FormItem>
        </i-col>
      </Row>
      <Row class="mt-5">
        <i-col :md="12">
          <strong> {{ $t("Terms and condition") }} </strong>
        </i-col>
        <i-col :md="12">
          <p>{{ $t("Number of accesses") }}</p>
          <FormItem prop="contract_user_limit">
            <i-select v-model="value.form.contract_user_limit" :placeholder="$t('Select number')">
              <i-option v-for="(range, idx) in rangeAccess" :key="idx" :value="range.value">
                {{ range.name }}
              </i-option>
            </i-select>
          </FormItem>
          <p class="mt-4">{{ $t("Valid domain or email") }}</p>
          <FormItem prop="domains_or_emails" :error="domainError">
            <vue-tags-input
              v-model="domain"
              :tags="domainTags"
              :validation="domainValidator"
              :add-on-blur="false"
              placeholder="domain.com"
              @tags-changed="onTagsChanged"
            />
          </FormItem>
        </i-col>
      </Row>
      <input type="submit" hidden /> <!-- FIXME: make clean Press Enter submit handling -->
    </Form>
  </AppModal>
</template>

<script>
import AppModal from "@/components/common/AppModal";
import VueTagsInput from '@johmun/vue-tags-input';
import { buildRequiredRules } from "@/helpers/common.helper";

export default {
  name: "ContractModalForm",

  components: {
    AppModal,
    VueTagsInput
  },
  props: {
    value: {
      type: Object,
      required: true,
      default: {
        showModal: false,
        form: {},
      }
    },
    modalOnSubmit: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      formRules: [],
      rangeAccess: [
        {
          "name": "1 - 20",
          "value": 20,
        },
        {
          "name": "1 - 50",
          "value": 50,
        },
        {
          "name": "1 - 100",
          "value": 100,
        },
        {
          "name": "1 - 250",
          "value": 250,
        },
        {
          "name": this.$t('Unlimited'),
          "value": 999999
        }
      ],
      domainTags: [],
      domain: "",
      domainError: "",
      domainValidator: [
        {
          classes: 'invalid-domain-or-email',
          rule: ({text}) => {
            const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            const domainRegex = /^(((?!\-))(xn\-\-)?[a-z0-9\-_]{0,61}[a-z0-9]{1,1}\.)*(xn\-\-)?([a-z0-9\-]{1,61}|[a-z0-9\-]{1,30})\.[a-z]{2,}$/
            return !(emailRegex.test(text) || domainRegex.test(text))
          },
          disableAdd: true,
        },
      ],
    };
  },
  watch: {
    'value.showModal'(newValue) {
      if (newValue === false) {
        this.domainTags = []
        this.domain = ""
        this.domainError = ""
        this.$emit("hide")
      }
      else {
        // Rebuild tags from the current form value (reset first — never accumulate
        // across re-opens), de-duplicated.
        const existing = this.value.form.domains_or_emails || []
        const seen = new Set()
        this.domainTags = []
        existing.forEach((domain_or_email) => {
          const text = (domain_or_email || "").trim().toLowerCase()
          if (text && !seen.has(text)) {
            seen.add(text)
            this.domainTags.push({ text })
          }
        })
        // Keep the form in sync with the de-duplicated tags.
        this.value.form.domains_or_emails = this.domainTags.map((t) => t.text)
      }
    }
  },
  created() {
    const requiredFields = {
      contract_name: null,
      contract_start_date: null,
      contract_end_date: null,
      contract_user_limit: null,
      domains_or_emails: null,
    }
    this.formRules = buildRequiredRules(requiredFields)
  },
  methods: {
    submitForm() {
      const contractFormElem = this.$refs.contractForm
      contractFormElem.validate( valid => {
        if (valid) this.modalOnSubmit()
      })
    },
    onTagsChanged(newTags) {
      // Single source of truth: vue-tags-input hands us the full, current list on
      // every add/delete. We normalize + de-duplicate here, then mirror it to the
      // form. This avoids the previous double-add / accumulation bug.
      const seen = new Set()
      const cleaned = []
      newTags.forEach((tag) => {
        const text = (tag.text || "").trim().toLowerCase()
        if (!text) return
        if (tag.tiClasses && tag.tiClasses.includes('ti-invalid') && !tag.tiClasses.includes('ti-duplicate')) {
          return // drop format-invalid entries
        }
        if (seen.has(text)) return // drop duplicates silently
        seen.add(text)
        cleaned.push({ text })
      })
      this.domainTags = cleaned
      this.value.form.domains_or_emails = cleaned.map((t) => t.text)
    },
    triggerCancel() {
      this.value.showModal = false;
      this.$refs["contractForm"].resetFields();
    },
  },
};
</script>

<style>
.ti-tag.ti-valid {
  background-color: #6864F3;
  color: #fff;
  border-radius: 7px;
  display: flex;
  padding: 3px 5px;
  margin: 2px;
}
</style>
